import surfaceMiningJson from "../content/surface-mining.json";
import { SeededRandom, hashSeed } from "./rng";
import type { SurfaceVeinSize, SurfaceVeinViewState } from "./phase-three-types";

export interface SurfaceMiningSizeConfig {
  count: number;
  radius: number;
  amount: number;
}

export interface SurfaceMiningConfig {
  version: 1;
  settlementIntervalMs: number;
  baseAmountPerSettlement: number;
  worldMargin: number;
  coreClearance: number;
  spawnClearance: number;
  objectiveClearance: number;
  defenseClearance: number;
  veinClearance: number;
  sizes: Record<SurfaceVeinSize, SurfaceMiningSizeConfig>;
}

export interface SurfaceMiningCircleExclusion {
  x: number;
  y: number;
  radius: number;
}

export interface SurfaceMiningRectangleExclusion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SurfaceVeinState extends SurfaceVeinViewState {}

export interface GenerateSurfaceVeinsOptions {
  seed: string;
  worldWidth: number;
  worldHeight: number;
  core: { x: number; y: number };
  spawns: Array<{ x: number; y: number }>;
  objectives: Array<{ x: number; y: number }>;
  defenses: SurfaceMiningRectangleExclusion[];
}

export const SURFACE_MINING_CONFIG = structuredClone(
  surfaceMiningJson,
) as SurfaceMiningConfig;

export function validateSurfaceMiningConfig(
  config: SurfaceMiningConfig = SURFACE_MINING_CONFIG,
): string[] {
  const errors: string[] = [];
  const positive = (value: number): boolean =>
    Number.isFinite(value) && value > 0;
  if (config.version !== 1) errors.push("地表采矿配置版本必须为1。");
  if (!positive(config.settlementIntervalMs)) {
    errors.push("地表采矿基础结算间隔必须大于0。");
  }
  if (!positive(config.baseAmountPerSettlement)) {
    errors.push("地表采矿单次基础量必须大于0。");
  }
  for (const key of [
    "worldMargin",
    "coreClearance",
    "spawnClearance",
    "objectiveClearance",
    "defenseClearance",
    "veinClearance",
  ] as const) {
    if (!positive(config[key])) errors.push(`地表采矿 ${key} 必须大于0。`);
  }
  for (const size of ["small", "medium", "large"] as const) {
    const entry = config.sizes[size];
    if (!Number.isInteger(entry?.count) || entry.count < 1) {
      errors.push(`${size}矿脉数量必须是正整数。`);
    }
    if (!positive(entry?.radius) || !positive(entry?.amount)) {
      errors.push(`${size}矿脉半径与储量必须大于0。`);
    }
  }
  return errors;
}

export function getSurfaceMiningBaseGoldTotal(
  config: SurfaceMiningConfig = SURFACE_MINING_CONFIG,
): number {
  return (["small", "medium", "large"] as const).reduce(
    (sum, size) => sum + config.sizes[size].count * config.sizes[size].amount,
    0,
  );
}

export function generateSurfaceVeins(
  options: GenerateSurfaceVeinsOptions,
  config: SurfaceMiningConfig = SURFACE_MINING_CONFIG,
): SurfaceVeinState[] {
  const errors = validateSurfaceMiningConfig(config);
  if (errors.length > 0) {
    throw new Error(`地表采矿配置无效：\n${errors.join("\n")}`);
  }
  const random = new SeededRandom(
    hashSeed(`${options.seed}:surface-mining:p4.13`),
  );
  const sizes: SurfaceVeinSize[] = [
    ...Array(config.sizes.small.count).fill("small" as const),
    ...Array(config.sizes.medium.count).fill("medium" as const),
    ...Array(config.sizes.large.count).fill("large" as const),
  ];
  for (let index = sizes.length - 1; index > 0; index -= 1) {
    const swap = random.integer(0, index);
    [sizes[index], sizes[swap]] = [sizes[swap], sizes[index]];
  }

  const veins: SurfaceVeinState[] = [];
  for (const size of sizes) {
    const definition = config.sizes[size];
    let placed: SurfaceVeinState | undefined;
    for (let attempt = 0; attempt < 4_000; attempt += 1) {
      const x = Math.round(
        config.worldMargin +
          random.next() * (options.worldWidth - config.worldMargin * 2),
      );
      const y = Math.round(
        config.worldMargin +
          random.next() * (options.worldHeight - config.worldMargin * 2),
      );
      const candidate: SurfaceVeinState = {
        id: veins.length + 1,
        size,
        x,
        y,
        radius: definition.radius,
        remaining: definition.amount,
        maxAmount: definition.amount,
      };
      if (isSurfaceVeinPlacementValid(candidate, veins, options, config)) {
        placed = candidate;
        break;
      }
    }
    if (!placed) {
      throw new Error(`无法为第 ${veins.length + 1} 处${size}矿脉找到安全位置。`);
    }
    veins.push(placed);
  }
  return veins;
}

export function isSurfaceVeinPlacementValid(
  candidate: Pick<SurfaceVeinState, "x" | "y" | "radius">,
  existing: Array<Pick<SurfaceVeinState, "x" | "y" | "radius">>,
  options: GenerateSurfaceVeinsOptions,
  config: SurfaceMiningConfig = SURFACE_MINING_CONFIG,
): boolean {
  if (
    candidate.x - candidate.radius < config.worldMargin ||
    candidate.y - candidate.radius < config.worldMargin ||
    candidate.x + candidate.radius > options.worldWidth - config.worldMargin ||
    candidate.y + candidate.radius > options.worldHeight - config.worldMargin
  ) {
    return false;
  }
  if (
    Math.hypot(candidate.x - options.core.x, candidate.y - options.core.y) <
    candidate.radius + config.coreClearance
  ) {
    return false;
  }
  if (
    options.spawns.some(
      (spawn) =>
        Math.hypot(candidate.x - spawn.x, candidate.y - spawn.y) <
        candidate.radius + config.spawnClearance,
    )
  ) {
    return false;
  }
  if (
    options.objectives.some(
      (objective) =>
        Math.hypot(candidate.x - objective.x, candidate.y - objective.y) <
        candidate.radius + config.objectiveClearance,
    )
  ) {
    return false;
  }
  if (
    options.defenses.some(
      (defense) =>
        circleIntersectsRectangle(
          candidate.x,
          candidate.y,
          candidate.radius + config.defenseClearance,
          defense,
        ),
    )
  ) {
    return false;
  }
  return existing.every(
    (vein) =>
      Math.hypot(candidate.x - vein.x, candidate.y - vein.y) >=
      candidate.radius + vein.radius + config.veinClearance,
  );
}

function circleIntersectsRectangle(
  x: number,
  y: number,
  radius: number,
  rectangle: SurfaceMiningRectangleExclusion,
): boolean {
  const closestX = Math.max(
    rectangle.x - rectangle.width / 2,
    Math.min(x, rectangle.x + rectangle.width / 2),
  );
  const closestY = Math.max(
    rectangle.y - rectangle.height / 2,
    Math.min(y, rectangle.y + rectangle.height / 2),
  );
  return Math.hypot(x - closestX, y - closestY) <= radius;
}
